import React, { useState, useRef } from 'react';
import { Upload, X, Loader2, Image as ImageIcon } from 'lucide-react';
import { Button } from './button';
import { Input } from './input';
import { uploadAPI } from '../../services/api';

const ImageUpload = ({ value, onChange, className = '' }) => {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [useUrl, setUseUrl] = useState(!value || value.startsWith('http'));
  const fileInputRef = useRef(null);

  const handleFileSelect = async (file) => {
    if (!file) return;
    
    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      alert('Please select a valid image file (JPEG, PNG, GIF, or WebP)');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('File size must be less than 5MB');
      return;
    }

    setUploading(true);
    try {
      const response = await uploadAPI.upload(file);
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const imageUrl = `${backendUrl}${response.data.url}`;
      onChange(imageUrl);
    } catch (error) {
      console.error('Upload error:', error);
      alert('Failed to upload image. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    handleFileSelect(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleFileInput = (e) => {
    const file = e.target.files[0];
    handleFileSelect(file);
  };

  const clearImage = () => {
    onChange('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className={className}>
      {/* Toggle between URL and Upload */}
      <div className="flex gap-2 mb-3">
        <Button
          type="button"
          variant={!useUrl ? 'default' : 'outline'}
          size="sm"
          onClick={() => setUseUrl(false)}
          className={!useUrl ? 'bg-amber-500 text-black hover:bg-amber-600' : 'border-zinc-700 text-zinc-300'}
        >
          <Upload className="h-4 w-4 mr-1" />
          Upload
        </Button>
        <Button
          type="button"
          variant={useUrl ? 'default' : 'outline'}
          size="sm"
          onClick={() => setUseUrl(true)}
          className={useUrl ? 'bg-amber-500 text-black hover:bg-amber-600' : 'border-zinc-700 text-zinc-300'}
        >
          <ImageIcon className="h-4 w-4 mr-1" />
          URL
        </Button>
      </div>

      {useUrl ? (
        /* URL Input */
        <Input
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="https://example.com/image.jpg"
          className="bg-zinc-800 border-zinc-700 text-white"
        />
      ) : (
        /* Upload Area */
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          className={`
            relative border-2 border-dashed rounded-xl p-6 text-center transition-all cursor-pointer
            ${dragOver ? 'border-amber-500 bg-amber-500/10' : 'border-zinc-700 hover:border-zinc-600'}
            ${uploading ? 'pointer-events-none' : ''}
          `}
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp"
            onChange={handleFileInput}
            className="hidden"
          />
          
          {uploading ? (
            <div className="flex flex-col items-center gap-2">
              <Loader2 className="h-8 w-8 text-amber-500 animate-spin" />
              <p className="text-zinc-400 text-sm">Uploading...</p>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2">
              <Upload className="h-8 w-8 text-zinc-500" />
              <p className="text-zinc-400 text-sm">
                Drag & drop an image or click to browse
              </p>
              <p className="text-zinc-500 text-xs">
                JPEG, PNG, GIF, WebP (max 5MB)
              </p>
            </div>
          )}
        </div>
      )}

      {/* Preview */}
      {value && (
        <div className="mt-4 relative inline-block">
          <img
            src={value}
            alt="Preview"
            className="w-32 h-32 rounded-lg object-cover border border-zinc-700"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={clearImage}
            className="absolute -top-2 -right-2 h-6 w-6 bg-red-500 hover:bg-red-600 text-white rounded-full"
          >
            <X className="h-3 w-3" />
          </Button>
        </div>
      )}
    </div>
  );
};

export default ImageUpload;
